/*
 * @Description:
 * @Author: Luke Z
 * @Date: 2021-03-16 10:52:49
 * @LastEditors: Jack Deng
 * @LastEditTime: 2021-11-16 18:10:02
 * @FilePath: /unifyxp-v2-store-portal/src/constrains/SalesChannelPreviewPageComponents/PageWrapper/PageWrapper.tsx
 */
/**
 *  Page contains Header.
 *  Author: Luke
 *  CreatedAt: 2021-03-05
 */

import React from 'react';
import { headerHeight } from 'src/layout/Components/config';
import css from './css.less';

import Warning from '@/src/components/Antd/Modal/WarningModal';
import {
  HomeLayout,
  SalesChannelHeader,
} from '@/src/layout/SalesChannelEditorLayout/HomeLayout';
import {
  ISalesChannelContent,
  SalesChannelStatus,
} from '@/src/types/common/SalesChannel';
import { get } from 'lodash';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import CollapseIcon from './Components/CollapseIcon';

interface IHeaderConfig {
  hasModified: boolean;
  submit?: () => {
    submitFunction: () => void;
    isDataValid: boolean;
  };
  salesChannelContentData: ISalesChannelContent | any;
  salesChannelName: string;
  onDiscard?: () => void;
  onGoBack?: () => void;
  hideMultiLanguage?: boolean;
  hasStyleSelect?: boolean;
  submitLoading?: boolean;
  translationEnabled?: boolean | undefined;
  handleOnTranslate?: (obj: any) => void;
  translationLoading?: boolean | undefined;
}
interface IProps {
  leftChild?: JSX.Element;
  rightChild?: JSX.Element;
  headerProps?: Record<string, unknown>;
  headerConfig?: IHeaderConfig;
  avoidSubmitWhenHasError?: boolean;
  customHeader?: JSX.Element;
  children?: any;
  customSubmit?;
  hideRightPart?: boolean;
  rarePart?: any;
  withoutCollapse?: boolean;
}

const PageWrapper = (props: IProps) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const [collapsed, setCollapsed] = React.useState(false);
  const {
    leftChild,
    rightChild,
    headerProps,
    headerConfig,
    avoidSubmitWhenHasError,
    customHeader,
    children,
    customSubmit,
    hideRightPart,
    rarePart,
    withoutCollapse,
  } = props;
  const { submit, translationEnabled, handleOnTranslate, translationLoading } =
    headerConfig;
  const [draftWarning, setDraftWarning] = React.useState({
    onOk: null,
    visible: false,
  });

  const [scheduledWarning, setScheduledWarning] = React.useState({
    visible: false,
  });
  const salesChannelStatus: SalesChannelStatus = React.useMemo(() => {
    const status = headerConfig.salesChannelContentData.status;
    if (status === 'Live') {
      return SalesChannelStatus.live;
    } else if (status === 'Scheduled') {
      return SalesChannelStatus.scheduled;
    } else {
      return SalesChannelStatus.draft;
    }
  }, [headerConfig.salesChannelContentData.status]);

  const constructSubmit = () => {
    if (submit) {
      const { submitFunction, isDataValid } = submit();

      if (isDataValid) {
        submitFunction();
      } else {
        if (avoidSubmitWhenHasError && !isDataValid) {
          return;
        }
        if (salesChannelStatus === SalesChannelStatus.draft) {
          setDraftWarning({
            visible: true,
            onOk: () => {
              submitFunction();
              handleOnDraftWarningClose();
            },
          });
        }
        if (salesChannelStatus === SalesChannelStatus.scheduled) {
          setScheduledWarning({
            visible: true,
          });
        }
        if (salesChannelStatus === SalesChannelStatus.live) {
          setScheduledWarning({
            visible: true,
          });
        }
        return;
      }
    }
    return;
  };

  const headerData = {
    title: headerConfig.salesChannelName,
    description: headerConfig.salesChannelContentData.versionName,
    status: salesChannelStatus,
    hasModified: headerConfig.hasModified,
    ...((submit || customSubmit) && {
      submit: customSubmit || constructSubmit,
    }),
    onDiscard: headerConfig.onDiscard,
    onGoBack: headerConfig.onGoBack,
    hideMultiLanguage: headerConfig.hideMultiLanguage,
    hasStyleSelect: headerConfig.hasStyleSelect,
    submitLoading: headerConfig.submitLoading,
  };

  const handleOnDraftWarningClose = () => {
    setDraftWarning({
      visible: false,
      onOk: null,
    });
  };

  const handleOnScheduledWarningClose = () => {
    setScheduledWarning({
      visible: false,
    });
  };

  return (
    <div className={css.pageWrapper}>
      <Warning
        visible={draftWarning.visible}
        header={getI18n('general.warning')}
        onOk={draftWarning.onOk}
        okText={getI18n('general.save')}
        cancelText={getI18n('general.commonHeader.discard_cancel')}
        onCancel={handleOnDraftWarningClose}>
        {getI18n('general.draftVersionWarning')}
      </Warning>
      <Warning
        visible={scheduledWarning.visible}
        header='Unable to save changes'
        cancelText={getI18n('general.commonHeader.discard_cancel')}
        onCancel={handleOnScheduledWarningClose}>
        {getI18n('general.fixErrorsBeforeSave')}
      </Warning>
      <div
        className={css.header}
        // headerProps
        style={{
          top: headerHeight,
          ...(headerProps || {}),
        }}>
        {customHeader || (
          <SalesChannelHeader
            title={headerData.title}
            description={headerData.description}
            status={headerData.status}
            hasModified={headerData.hasModified}
            submit={headerData.submit}
            onDiscard={headerData.onDiscard}
            onGoBack={headerData.onGoBack}
            hideMultiLanguage={headerData.hideMultiLanguage}
            hasStyleSelect={headerData.hasStyleSelect}
            submitLoading={headerData.submitLoading}
            onContentReverse={get(headerConfig, 'onContentReverse')}
            translationEnabled={translationEnabled}
            onChangeTranslation={handleOnTranslate}
            translationLoading={translationLoading}
            rarePart={
              rarePart ||
              (!withoutCollapse && (
                <CollapseIcon collapsed={collapsed} onChange={setCollapsed} />
              ))
            }
          />
        )}
      </div>
      {(leftChild || rightChild) && (
        <HomeLayout
          leftChild={leftChild}
          rightChild={rightChild}
          hideRight={hideRightPart || collapsed}
        />
      )}
      {children}
    </div>
  );
};
export default PageWrapper;
